/**
 * Cycle 416 — retry policy and failure shape for an alarm ACK (Snooze / Stop).
 *
 * ⚠ WHY THIS EXISTS. Before this cycle every ack POST on the alarm path sat
 * inside a `catch {}` that swallowed the throw. A rejected or undelivered ack
 * left the optimistic flip in place and told the user NOTHING: the banner read
 * "Snoozing 5:00" or dropped its controls, and the caregiver believed the alarm
 * was handled. Cycle 412 closed the one CAUSE that was making acks bounce (a
 * quieting action addressed to an already-recovered row now refuses `409
 * episode_closed`); this module is about every OTHER way an ack can fail, none
 * of which the user could previously see.
 *
 * ⚠ WHAT THIS DELIBERATELY DOES **NOT** CHANGE. The cycle-409 rule stands: a
 * NETWORK fault keeps the optimistic local silence, because the local user is at
 * least not stuck on a sounding alarm, and the bounded 5-minute window means a
 * lost frame can only lapse early, never late. This module does not shorten,
 * lengthen or remove that silence. It makes the failure RETRIED and VISIBLE.
 *
 * Pure and framework-free on purpose — the policy is unit-testable without
 * React, a timer, or a socket.
 */

/** Which control the user pressed. `stop` covers the /silence POST. */
export type AckAction = "snooze" | "stop";

/**
 * Why the ack did not land.
 *
 *  - `network` — the request never got an answer (offline, timeout, throw).
 *    Retryable. The optimistic silence is KEPT (cycle 409).
 *  - `rejected` — the SERVER answered and said no. Authoritative, never
 *    retried; the caller withdraws the optimistic flip and sounds again.
 */
export type AckFailureReason = "network" | "rejected";

export interface AckFailure {
  action: AckAction;
  reason: AckFailureReason;
  /** HTTP status when the server answered; null for a network fault. */
  status: number | null;
  /** How many attempts were made in total (1 = no retry happened). */
  attempts: number;
  /** Server-supplied refusal code when there was one (e.g. `episode_closed`). */
  code: string | null;
}

/**
 * Total attempts for one ack, including the first. Bounded (§3.3): a caregiver
 * holding a phone with no signal must not generate an unbounded request stream
 * against the alarm endpoints, and an ack that has not landed in ~5 s is a
 * failure the user needs to SEE rather than one worth hiding behind more
 * retries.
 */
export const ACK_MAX_ATTEMPTS = 4;

/**
 * Backoff before attempt N+1, in ms. Three waits for four attempts; the whole
 * ladder completes in ~4.6 s, comfortably inside the bounded local silence so
 * the user learns the outcome long before the optimistic window lapses.
 */
export const ACK_BACKOFF_MS: readonly number[] = [400, 1200, 3000];

/**
 * ⚠ ONLY a fault with no answer is retried.
 *
 * A server that ANSWERED has spoken authoritatively and its refusal is the
 * truth: `409 episode_closed` (a stale slot), `409 not_silenceable_urgent_low`
 * (Urgent Low is Snooze-only — retrying could never turn that into a Stop),
 * `404`, `401`, `422`. Retrying any of those cannot change the outcome and
 * would only hammer the alarm endpoints.
 *
 * ⚠ `rate_limit` (429) is NOT retryable, and that is the point — the cycle-409
 * quiet door throttles deliberately, and a client that retried into a 429 is
 * exactly the request flood the security review forbids.
 *
 * ⚠ `server` (5xx) IS ALSO NOT RETRYABLE, AND THIS IS A SAFETY DECISION, NOT A
 * CLASSIFICATION ONE. A 5xx is tempting to treat as "no answer" — the server
 * decided nothing and wrote nothing. But before cycle 416 every call site
 * withdrew the optimistic mute on the first non-`network` answer, and a 5xx
 * satisfied that: the alarm re-armed after ONE round trip. Retrying it would
 * hold the local mute for three more round trips plus ~4.6 s of backoff — and
 * `apiClient.request` sets no timeout, so a slow-failing 504 could stretch that
 * to minutes. That is a REAL reduction in audibility, on the rail this cycle
 * exists to protect, and it would have made the claim "only visibility changed"
 * false. So a 5xx keeps its cycle-409 behaviour exactly: withdraw and sound.
 *
 * Net: the ladder retries a fault where the request got NO answer at all —
 * offline, timeout, a thrown fetch — and nothing else.
 */
export function ackIsRetryable(kind: string): boolean {
  return kind === "network";
}

/**
 * Wait before the next attempt, or null when the budget is spent.
 * `attemptsSoFar` is 1-based (1 = the first attempt has just failed).
 */
export function ackBackoffMs(attemptsSoFar: number): number | null {
  if (attemptsSoFar < 1) return null;
  if (attemptsSoFar >= ACK_MAX_ATTEMPTS) return null;
  return ACK_BACKOFF_MS[attemptsSoFar - 1] ?? null;
}

/**
 * The user-facing sentence for a failed ack. Deliberately plain, and it never
 * claims the alarm is handled.
 *
 * ⚠ NO PHI: no member name, no glucose value, no contact, no token — a banner
 * string is rendered on a lock screen and read by whoever is holding the phone.
 */
export function ackFailureMessage(f: AckFailure): string {
  const what = f.action === "snooze" ? "Snooze" : "Stop";
  if (f.reason === "network") {
    return `${what} didn’t reach the server — the alarm may still be active.`;
  }
  if (f.code === "episode_closed") {
    return `That alarm already ended — ${what.toLowerCase()} was not applied.`;
  }
  if (f.code === "not_silenceable_urgent_low") {
    return "Urgent Low can only be snoozed, not stopped.";
  }
  return `${what} was refused by the server — the alarm is still active.`;
}
